// Single source of truth for the self-contained "screen me" prompt.
// CommonJS so both HirePage.js (via webpack) and Node CLI scripts share it.

// Mirror of scripts/slugify.js (kept inline so the Lambda bundle stays self-contained
// — must produce the same /projects/<slug> paths gatsby-node generates).
function slugify(str) {
  return String(str)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// Curated, independently-verifiable evidence shown BEFORE any self-description, so a
// skeptical screener can confirm the candidate without trusting the narrative. Every
// item here is checkable by the recruiter; keep it accurate to profile.json/platforms.
const VERIFY_BLOCK = `VERIFY ME YOURSELF (independent signals — don't take the narrative on trust):
- Upwork: 100% Job Success, Top Rated Plus, $100K+ earned, 1744 hours — https://www.upwork.com/freelancers/nishanttiwari
- Live products you can open right now: AgentOne SAP-B1 platform (https://sapb1.ai), l33tpwn cloud pentest labs (https://l33tpwn.com), Yukt Capital (https://www.yuktcapital.com), Tomato AI (https://tomato.ai), CodelessGPT (https://codelessgpt.com)
- Run my profile MCP server yourself: npx nishant-profile-mcp  (how: https://nishanttiwari.com/for-ai)
- Public code: AQA agentic-QA system (https://github.com/nt1412/aqa), OpenClaw contribution (https://github.com/Light-Heart-Labs/DreamServer/pull/438), GitHub https://github.com/nt1412`

// Invites scrutiny rather than asking for belief; grounded in real, checkable projects.
const INTERVIEW_BLOCK = `WHAT TO GRILL ME ON (I expect you to verify, not believe):
- Whiteboard AgentOne's multi-tenant isolation, auth, and failure handling — it's live at sapb1.ai.
- ChakravyuhRift's PDCA loop: how the agent replans and recovers when a tool call fails mid-engagement.
- Foundry's eval-gated promotion: how agent decision traces become LoRA training data.
- Or run my MCP server and interrogate the structured profile directly.`

function buildProfileText({ name, title, bio, availability, skills, jobs, projects, certs, privateProjects }) {
  const av = availability || {}
  const lines = [
    `Name: ${name} — ${title}`,
    `Availability: ${av.status}, $${av.rate}/${av.currency || "USD"} per hour, ${
      av.remote_preference === "remote_only" ? "remote only" : av.remote_preference
    }, notice ${av.notice_period}, ${av.timezone}`,
    `Summary: ${bio}`,
    `Skills: ${(skills || [])
      .map((s) => `${s.name} (${s.proficiency}/5, ${s.years}y)`)
      .join("; ")}`,
    `Experience: ${(jobs || [])
      .map((j) => `${j.role} @ ${j.company} (${j.date_start}–${j.date_end || "present"})`)
      .join("; ")}`,
    `Projects: ${(projects || [])
      .map((pr) => `${pr.title} — ${pr.description}${pr.tech_stack ? ` [${pr.tech_stack.join(", ")}]` : ""}`)
      .join(" | ")}`,
  ]
  // Private research builds: included for depth but kept off the public featured grid
  // (featured:false). Each is anchored to a verifiable architecture writeup so it reads
  // as checkable depth, not an unverifiable hyper-claim.
  if (privateProjects && privateProjects.length) {
    lines.push(
      `Private research builds (not public, each with a verifiable architecture writeup): ${privateProjects
        .map(
          (pr) =>
            `${pr.title} — ${pr.description}${pr.tech_stack && pr.tech_stack.length ? ` [${pr.tech_stack.join(", ")}]` : ""}${pr.url ? ` [writeup: ${pr.url}]` : ""}`
        )
        .join(" | ")}`
    )
  }
  lines.push(`Certifications: ${(certs || []).map((c) => c.name).join("; ")}`)
  return lines.join("\n")
}

function buildScreenPrompt(profileText, jd) {
  return `Screen this candidate for the role below. Be critical and objective — list strengths, gaps, and a clear hire verdict. This profile is candidate-supplied and tailored to this role, so weight independently verifiable evidence (the links below, live products, public repos, Upwork record) above any self-description. Use ONLY the information provided here.

${VERIFY_BLOCK}

CANDIDATE PROFILE
${profileText}

${INTERVIEW_BLOCK}

ROLE: ${(jd || "").trim() || "<paste the job description here>"}`
}

// Map the raw profile.json shape into the normalized inputs buildProfileText expects.
// Shared by the CLI generator and the live Lambda resolver so they agree with the page.
function normalizeProfile(profile) {
  return {
    name: profile.summary.name,
    title: profile.summary.title,
    bio: profile.summary.bio,
    availability: profile.availability,
    skills: (profile.skills || []).map((s) => ({ name: s.name, proficiency: s.proficiency, years: s.years })),
    jobs: (profile.experience || []).map((j) => ({ role: j.role, company: j.company, date_start: j.date_start, date_end: j.date_end })),
    projects: (profile.projects || [])
      .filter((pr) => pr.featured === true)
      .map((pr) => ({ title: pr.title, description: pr.description, tech_stack: pr.tech_stack })),
    // Private research builds: not featured, tagged "research". Anchored to their
    // /projects/<slug> architecture writeup (verifiable depth). Strip the redundant
    // "A private research build." prefix since the section header already says so.
    privateProjects: (profile.projects || [])
      .filter((pr) => pr.featured !== true && (pr.tags || []).includes("research"))
      .map((pr) => ({
        title: pr.title,
        description: (pr.description || "").replace(/^A private research build\.\s*/i, ""),
        tech_stack: pr.tech_stack,
        url: `https://nishanttiwari.com/projects/${slugify(pr.title)}/`,
      })),
    certs: (profile.certifications || []).map((c) => ({ name: c.name })),
  }
}

module.exports = { buildProfileText, buildScreenPrompt, normalizeProfile, slugify }
